import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  AlertTitle,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  HighlightOff as HighlightOffIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { storage } from '../../../firebase';
import { getBranches as getBranchesAdapter } from '../../../services/migration/branchDataService';
import { taskDataService } from '../../../services/taskDataService';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

interface CreateTeamTaskModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  teams: any[];
  personnel: any[];
  taskGroups: any[];
  projectSqlIdToEdit?: number | null;
  onSaved?: () => void;
}

// Görev önceliği seçenekleri
const priorityOptions = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' }
];

// Görev tipi arayüzü
interface TeamTask {
  id: string;
  sqlId?: number; // SQL görev id'si (düzenleme: mevcut görevler için)
  teamMemberId: string;
  teamMemberName: string;
  taskName: string;
  priority: string;
  dueDate: string;
  dueTime: string;
  taskOrder?: number; // Sadece sıralı görevler için
  description?: string; // Görev açıklaması için ekledik
  taskImages?: {preview: string, file: File | null}[]; // Görev resimleri için ekledik
  existingImageUrls?: string[]; // Mevcut resim URL'leri
}

// Görev durumu için tip
type TaskStatus = 'pending' | 'accepted' | 'started' | 'paused' | 'completed';

// Görev verisi tipi
interface TaskData {
  name: string;
  teamMemberId: string;
  teamMemberName: string;
  priority: string;
  dueDate: string;
  status: TaskStatus;
  createdAt: number;
  taskOrder?: number;
  groupId?: string;
  groupName?: string;
  branchId?: string;
  branchName?: string;
  description?: string; // Görev açıklaması
  imageUrls?: string[]; // Görev resim URL'leri için eklendi
  statusHistory: {
    pending: number;       // Oluşturulma zamanı
    accepted?: number;     // Kabul edilme zamanı
    started?: number;      // Başlama zamanı
    paused?: number;       // Durdurulma zamanı
    resumed?: number;      // Yeniden başlama zamanı
    completed?: number;    // Tamamlanma zamanı
  };
  canStart: boolean;       // Başlanabilir mi (sıralı görevler için)
}

// Proje verisi tipi
interface ProjectData {
  name: string;
  teamId: string;
  teamName: string;
  createdAt: number;
  isSequential: boolean;
  status: string;
  tasks: Record<string, TaskData>;
  groupId?: string;
  groupName?: string;
  branchId?: string;
  branchName?: string;
  activeTaskOrder?: number;  // Sıralı görevler için aktif görev sırası
  updatedAt?: number; // Güncellenme tarihi
}

const CreateTeamTaskModal: React.FC<CreateTeamTaskModalProps> = ({
  open,
  onClose,
  companyId,
  teams,
  personnel,
  taskGroups,
  projectSqlIdToEdit,
  onSaved,
}) => {
  const isEditMode = !!projectSqlIdToEdit;
  // Düzenleme: silinen görevleri tespit için orijinal görev sqlId'leri
  const [originalTaskSqlIds, setOriginalTaskSqlIds] = useState<number[]>([]);

  // Form state'leri
  const [projectName, setProjectName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<any | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [localTeams, setLocalTeams] = useState<any[]>([]); // Ekipleri yerel olarak saklamak için
  const [isSequentialSystem, setIsSequentialSystem] = useState(true); // Varsayılan olarak sıralı sistem
  const [isNormalSystem, setIsNormalSystem] = useState(false);
  
  // Görevler için state
  const [tasks, setTasks] = useState<TeamTask[]>([]);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskMemberId, setNewTaskMemberId] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskDueTime, setNewTaskDueTime] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  
  // Tablo pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Yükleme ve hata durumları
  const [loading, setLoading] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // Form doğrulama hataları
  const [formErrors, setFormErrors] = useState({
    projectName: '',
    team: '',
    tasks: ''
  });

  // Resim ekleme için state'ler
  const [taskImages, setTaskImages] = useState<Array<{
    file: File | null,
    preview: string,
    uploading: boolean,
    error: string
  }>>([
    { file: null, preview: '', uploading: false, error: '' },
    { file: null, preview: '', uploading: false, error: '' }
  ]);

  // Ekipleri ve Şubeleri Firebase'den yükle
  useEffect(() => {
    if (!companyId || !open) return;
    
    const fetchInitialData = async () => {
      setLoadingInitialData(true);
      try {
        // Ekipler: SQL'den gelen `teams` prop'u (id=UI id, sqlId taşır). Şubeler: branchDataService (SQL-önce).
        let teamsList = teams;
        if (!teamsList || teamsList.length === 0) {
          const groups = await taskDataService.listTeamGroups();
          teamsList = groups.map((g) => ({ id: g.legacyFirebaseKey || String(g.id), sqlId: g.id, name: g.name }));
        }
        setLocalTeams(teamsList);

        const serviceBranches = await getBranchesAdapter(companyId);
        const branchesList = serviceBranches.map((b) => ({ id: b.id, name: b.name || 'İsimsiz Şube' }));
        setBranches(branchesList);
      } catch (error) {
        console.error('Başlangıç verileri yüklenirken hata:', error);
        setNotification({ open: true, message: 'Ekip ve şube verileri yüklenemedi.', severity: 'error' });
      } finally {
        setLoadingInitialData(false);
      }
    };
    
    fetchInitialData();
  }, [companyId, open, teams]);

  // Düzenleme modunda proje verilerini yükle
  useEffect(() => {
    const loadProjectForEditing = async () => {
      if (!isEditMode || !companyId || !projectSqlIdToEdit || localTeams.length === 0) return;
    
      setLoadingProject(true);
      try {
        const detail = await taskDataService.getTeamProject(projectSqlIdToEdit);
        if (detail) {
          const { project, tasks: projectTasks } = detail;

          setProjectName(project.name || '');
          setIsSequentialSystem(project.isSequential === true);
          setIsNormalSystem(project.isSequential === false);

          const foundTeam = localTeams.find(t => t.sqlId === project.teamGroupId);
          setSelectedTeam(foundTeam || null);

          const foundBranch = branches.find(b => b.id === project.branchId);
          setSelectedBranch(foundBranch || null);

          const foundGroup = taskGroups.find(g => g.sqlId === project.groupId);
          setSelectedGroup(foundGroup || null);

          const loadedTasks = (projectTasks || []).map((tk) => {
            const [dueDate, dueTime] = (tk.dueDate || '').split('T');
            let existingImageUrls: string[] = [];
            if (tk.imageUrls) {
              try {
                const parsed = JSON.parse(tk.imageUrls);
                existingImageUrls = Array.isArray(parsed) ? parsed : [];
              } catch {
                existingImageUrls = [];
              }
            }
            return {
              id: tk.legacyFirebaseKey || String(tk.id),
              sqlId: tk.id,
              taskName: tk.name || '',
              teamMemberId: tk.teamMemberUid || '',
              teamMemberName: tk.teamMemberName || 'Bilinmeyen',
              priority: tk.priority || 'medium',
              dueDate: dueDate || '',
              dueTime: dueTime || '',
              description: tk.description || '',
              taskOrder: tk.taskOrder ?? undefined,
              existingImageUrls,
            } as TeamTask;
          });
          loadedTasks.sort((a, b) => (a.taskOrder || 0) - (b.taskOrder || 0));
          setTasks(loadedTasks);
          setOriginalTaskSqlIds(loadedTasks.filter(t => t.sqlId).map(t => t.sqlId as number));
        } else {
          setNotification({ open: true, message: 'Düzenlenecek proje bulunamadı.', severity: 'error' });
          onClose();
        }
      } catch (error) {
        console.error('Proje yüklenirken hata:', error);
        setNotification({ open: true, message: 'Proje verileri yüklenirken bir hata oluştu.', severity: 'error' });
      } finally {
        setLoadingProject(false);
      }
    };
    
    if (isEditMode && open && !loadingInitialData) {
      loadProjectForEditing();
    }
  }, [isEditMode, open, companyId, projectSqlIdToEdit, localTeams, branches, taskGroups, loadingInitialData]);

  // Sayfalama değişikliği
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Senaryo tipi değişikliği
  const handleSequentialSystemChange = (checked: boolean) => {
    setIsSequentialSystem(checked);
    if (checked) {
      setIsNormalSystem(false);
      
      // Mevcut görevlere sıra numarası ekle
      const updatedTasks = tasks.map((task, index) => ({
        ...task,
        taskOrder: index + 1
      }));
      setTasks(updatedTasks);
    }
  };

  const handleNormalSystemChange = (checked: boolean) => {
    setIsNormalSystem(checked);
    if (checked) {
      setIsSequentialSystem(false);
      
      // Görevlerden sıra numarasını kaldır
      const updatedTasks = tasks.map(task => {
        const { taskOrder, ...rest } = task;
        return rest;
      });
      setTasks(updatedTasks);
    }
  };

  // Resim seçme ve işleme fonksiyonları
  const handleImageSelect = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const fileType = file.type;
    const fileSize = file.size;
    
    // Dosya tipi kontrolü (jpg, jpeg, png, webp)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    // Resmi önizleme için URL oluştur
    const preview = URL.createObjectURL(file);
    
    // Hata kontrolü
    let errorMessage = '';
    
    if (!allowedTypes.includes(fileType)) {
      errorMessage = 'Sadece JPG, JPEG, PNG ve WEBP formatları desteklenir.';
    }
    
    // Dosya boyutu kontrolü (max 1MB)
    const maxSize = 1 * 1024 * 1024; // 1MB
    if (fileSize > maxSize) {
      errorMessage = 'Dosya boyutu 1MB\'dan büyük olamaz.';
    }
    
    // State'i güncelle - Hata olsa bile resmi ekliyoruz ki kaldırabilelim
    const newImages = [...taskImages];
    newImages[index] = {
      file,
      preview,
      uploading: false,
      error: errorMessage
    };
    setTaskImages(newImages);
  };
  
  // Resmi kaldır
  const handleRemoveImage = (index: number) => {
    const newImages = [...taskImages];
    
    // Eğer önizleme URL'si varsa, tarayıcı belleğinden temizle
    if (newImages[index].preview) {
      URL.revokeObjectURL(newImages[index].preview);
    }
    
    newImages[index] = { file: null, preview: '', uploading: false, error: '' };
    setTaskImages(newImages);
  };

  // Yeni görev ekleme
  const handleAddTask = () => {
    if (!newTaskName.trim()) {
      setNotification({
        open: true,
        message: 'Görev adı boş olamaz',
        severity: 'error'
      });
      return;
    }
    
    if (!newTaskMemberId) {
      setNotification({
        open: true,
        message: 'Lütfen bir ekip üyesi seçin',
        severity: 'error'
      });
      return;
    }
    
    // Resim doğrulama
    const errorImages = taskImages.filter(img => img.error !== '' && img.preview !== '');
    if (errorImages.length > 0) {
      const errorMessages = errorImages.map((img, index) => `Resim ${index + 1}: ${img.error}`);
      setNotification({
        open: true,
        message: `Hatalı resimler var: ${errorMessages.join(', ')}. Lütfen bu resimleri kaldırın veya değiştirin.`,
        severity: 'error'
      });
      return;
    }
    
    const member = teamMembers.find(m => m.id === newTaskMemberId);
    const memberName = member ? member.name : 'Bilinmeyen Üye';
    
    const newTask: TeamTask = {
      id: Date.now().toString(), // Geçici ID
      teamMemberId: newTaskMemberId,
      teamMemberName: memberName,
      taskName: newTaskName,
      priority: newTaskPriority,
      dueDate: newTaskDueDate,
      dueTime: newTaskDueTime,
      description: newTaskDescription,
      taskImages: taskImages.filter(img => img.preview !== '' && img.error === '').map(img => ({
        preview: img.preview,
        file: img.file
      })),
      existingImageUrls: []
    };
    
    // Eğer sıralı sistem ise, sıra numarası ekle
    if (isSequentialSystem) {
      newTask.taskOrder = tasks.length + 1;
    }
    
    setTasks([...tasks, newTask]);
    
    // Form alanlarını temizle
    setNewTaskName('');
    setNewTaskPriority('medium');
    setNewTaskDescription('');
    // Resimleri temizle
    setTaskImages([
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' }
    ]);
    // Ekip üyesi seçimini ve tarihi korumak için onları sıfırlamıyoruz
  };

  // Görev silme
  const handleRemoveTask = (taskId: string) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    
    // Eğer sıralı sistem ise, görev silindikten sonra sıra numaralarını güncelle
    if (isSequentialSystem) {
      const reorderedTasks = updatedTasks.map((task, index) => ({
        ...task,
        taskOrder: index + 1
      }));
      setTasks(reorderedTasks);
    } else {
      setTasks(updatedTasks);
    }
  };

  // Projeyi kaydetme
  const handleSaveProject = async () => {
    // Form doğrulama
    const errors = {
      projectName: '',
      team: '',
      tasks: ''
    };
    
    if (!projectName.trim()) {
      errors.projectName = 'Proje adı gereklidir';
    }
    
    if (!selectedTeam) {
      errors.team = 'Lütfen bir ekip seçin';
    }
    
    if (tasks.length === 0) {
      errors.tasks = 'En az bir görev eklemelisiniz';
    }
    
    setFormErrors(errors);
    
    // Hata varsa işlemi durdur
    if (errors.projectName || errors.team || errors.tasks) {
      return;
    }
    
    if (!companyId) {
      setNotification({
        open: true,
        message: 'Şirket bilgisi bulunamadı',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // 1) Projeyi oluştur/güncelle (SQL)
      const projectInput = {
        name: projectName,
        teamGroupId: selectedTeam.sqlId as number,
        teamName: selectedTeam.name as string,
        groupId: selectedGroup?.sqlId ?? null,
        groupName: selectedGroup?.name ?? null,
        branchId: selectedBranch?.id ?? null,
        branchName: selectedBranch?.name ?? null,
      };

      let projectId: number;
      if (isEditMode && projectSqlIdToEdit) {
        await taskDataService.updateTeamProject(projectSqlIdToEdit, projectInput);
        projectId = projectSqlIdToEdit;
      } else {
        const created = await taskDataService.createTeamProject({
          ...projectInput,
          isSequential: isSequentialSystem,
        });
        projectId = created.id;
      }

      // 2) Görev başına resim yükle ve imageUrls (JSON string) hazırla
      const buildImageUrls = async (task: TeamTask): Promise<string | undefined> => {
        let allImageUrls: string[] = [...(task.existingImageUrls || [])];
        const files = (task.taskImages || []).filter(img => img.file !== null);
        if (files.length > 0) {
          const uploaded = await Promise.all(
            files.map(async (img, imgIndex) => {
              if (!img.file) return null;
              try {
                const imageRef = storageRef(storage, `companies/${companyId}/team-tasks/${projectId}/${task.id}/${imgIndex}.jpg`);
                const uploadResult = await uploadBytes(imageRef, img.file);
                return await getDownloadURL(uploadResult.ref);
              } catch (error) {
                console.error(`Resim yüklenirken hata (Görev ${task.id}, Resim ${imgIndex}):`, error);
                return null;
              }
            })
          );
          allImageUrls = [...allImageUrls, ...(uploaded.filter(u => u !== null) as string[])];
        }
        return allImageUrls.length > 0 ? JSON.stringify(allImageUrls) : undefined;
      };

      // 3) Görevleri uzlaştır (update mevcut / create yeni)
      for (let index = 0; index < tasks.length; index++) {
        const task = tasks[index];
        const dueDate = task.dueDate && task.dueTime
          ? `${task.dueDate}T${task.dueTime}`
          : task.dueDate ? task.dueDate : null;
        const imageUrls = await buildImageUrls(task);

        if (task.sqlId) {
          await taskDataService.updateTeamProjectTask(projectId, task.sqlId, {
            name: task.taskName,
            description: task.description || '',
            teamMemberUid: task.teamMemberId,
            teamMemberName: task.teamMemberName,
            priority: task.priority,
            dueDate,
            imageUrls,
            groupId: selectedGroup?.sqlId ?? null,
            branchId: selectedBranch?.id ?? null,
          });
        } else {
          await taskDataService.createTeamProjectTask(projectId, {
            name: task.taskName,
            description: task.description || '',
            teamMemberUid: task.teamMemberId,
            teamMemberName: task.teamMemberName,
            priority: task.priority,
            dueDate,
            taskOrder: isSequentialSystem ? (task.taskOrder || index + 1) : null,
            imageUrls,
            groupId: selectedGroup?.sqlId ?? null,
            branchId: selectedBranch?.id ?? null,
          });
        }
      }

      // 4) Düzenlemede çıkarılan görevleri sil
      if (isEditMode) {
        const currentSqlIds = new Set(tasks.filter(t => t.sqlId).map(t => t.sqlId as number));
        const removed = originalTaskSqlIds.filter(id => !currentSqlIds.has(id));
        for (const taskSqlId of removed) {
          await taskDataService.deleteTeamProjectTask(projectId, taskSqlId);
        }
      }

      setNotification({
        open: true,
        message: `Proje başarıyla ${isEditMode ? 'güncellendi' : 'oluşturuldu'}!`,
        severity: 'success'
      });

      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Proje oluşturulurken hata:', error);
      
      setNotification({
        open: true,
        message: 'Proje oluşturulurken bir hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Bildirim kapatma
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Formu sıfırla
  const resetForm = () => {
    setProjectName('');
    setSelectedTeam(null);
    setSelectedGroup(null);
    setSelectedBranch(null);
    setIsSequentialSystem(true);
    setIsNormalSystem(false);
    setTasks([]);
    setNewTaskName('');
    setNewTaskMemberId('');
    setNewTaskPriority('medium');
    setNewTaskDueDate('');
    setNewTaskDueTime('');
    setNewTaskDescription('');
    
    // Resimleri temizle
    taskImages.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setTaskImages([
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' }
    ]);
    
    setPage(0);
    setOriginalTaskSqlIds([]);
    setFormErrors({
      projectName: '',
      team: '',
      tasks: ''
    });
  };

  // Modal kapanırken formu sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Sayfalanmış görevler
  const paginatedTasks = tasks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Sabit boyutta gösterilecek tablo satırları
  const displayRows = [...paginatedTasks];
  const emptyRows = rowsPerPage - displayRows.length;
  
  // Bugünün tarihi (minimum değer için)
  const today = new Date().toISOString().split('T')[0];
  
  // Ekip üyeleri (seçili ekibe göre filtrelenmiş)
  const teamMembers = selectedTeam && selectedTeam.members 
    ? Object.entries(selectedTeam.members).map(([id, data]: [string, any]) => ({
        id,
        name: data.name,
        duty: data.duty,
        memberRole: data.memberRole
      }))
    : [];

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5
        }}>
          <Typography variant="h6" fontWeight="bold">{isEditMode ? 'Projeyi Düzenle' : 'Proje Oluştur'}</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 2, py: 2 }}>
          {loadingProject ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Proje verileri yükleniyor...</Typography>
            </Box>
          ) : (
            <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="primary" fontWeight="medium" gutterBottom>
              Proje Bilgileri
            </Typography>
            <Divider />
          </Box>
          
          <Grid container spacing={2}>
            {/* Proje adı ve Ekip seçimi (aynı satırda) */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Proje Adı"
                variant="outlined"
                fullWidth
                size="small"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  if (e.target.value.trim()) {
                    setFormErrors(prev => ({ ...prev, projectName: '' }));
                  }
                }}
                error={!!formErrors.projectName}
                helperText={formErrors.projectName}
                placeholder="Satış Projesi, Yazılım Projesi vb."
                InputProps={{
                  startAdornment: (
                    <AssignmentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                id="team-selector"
                options={localTeams} // Firebase'den gelen ekip verilerini kullan
                value={selectedTeam}
                onChange={(event, newValue) => {
                  setSelectedTeam(newValue);
                  if (newValue) {
                    setFormErrors(prev => ({ ...prev, team: '' }));
                  }
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          mr: 1, 
                          bgcolor: option.color || 'primary.main',
                          fontSize: '0.75rem'
                        }}
                      >
                        <GroupsIcon sx={{ fontSize: '1rem' }} />
                      </Avatar>
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ekip Seç"
                    placeholder="Ekip ara..."
                    size="small"
                    error={!!formErrors.team}
                    helperText={formErrors.team}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <GroupsIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                              {loadingInitialData ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                          ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                size="small"
                    loading={loadingInitialData}
                loadingText="Ekipler yükleniyor..."
                noOptionsText="Hiç ekip bulunamadı"
              />
            </Grid>
            
            {/* Şube ve Grup seçimi (aynı satırda) */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                id="branch-selector"
                options={branches}
                value={selectedBranch}
                onChange={(event, newValue) => {
                  setSelectedBranch(newValue);
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Şube Seç (Opsiyonel)"
                    placeholder="Şube ara..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                id="group-selector"
                options={taskGroups}
                value={selectedGroup}
                onChange={(event, newValue) => {
                  setSelectedGroup(newValue);
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Görev Grubu Seç (Opsiyonel)"
                    placeholder="Grup ara..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <CategoryIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                size="small"
              />
            </Grid>
            
            {/* Proje senaryosu seçimi */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isSequentialSystem}
                      onChange={(e) => handleSequentialSystemChange(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" fontWeight="medium">
                      Sıralı Görev Sistemi
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isNormalSystem}
                      onChange={(e) => handleNormalSystemChange(e.target.checked)}
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" fontWeight="medium">
                      Normal Ekip Görevi
                    </Typography>
                  }
                />

                <Tooltip title={
                  isSequentialSystem 
                    ? "Bu modda, ekip üyeleri görevleri sırayla tamamlar. Bir görev tamamlanmadan sonraki göreve geçilemez."
                    : "Bu modda, ekip üyeleri görevleri birbirlerinden bağımsız olarak tamamlayabilirler."
                }>
                  <IconButton size="small" color="primary">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          
          {formErrors.tasks && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formErrors.tasks}
            </Alert>
          )}
          
          {/* Görev ekleme formu */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Görev Ekle
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={isSequentialSystem ? 3 : 3}>
                  <TextField
                    label="Görev Adı"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={newTaskName}
                    onChange={(e) => setNewTaskName(e.target.value)}
                    placeholder="Görev adını girin"
                  />
                </Grid>
                
                <Grid item xs={12} md={isSequentialSystem ? 3 : 3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Öncelik</InputLabel>
                    <Select
                      value={newTaskPriority}
                      onChange={(e) => setNewTaskPriority(e.target.value)}
                      label="Öncelik"
                    >
                      {priorityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={isSequentialSystem ? 6 : 6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ekip Üyesi</InputLabel>
                    <Select
                      value={newTaskMemberId}
                      onChange={(e) => setNewTaskMemberId(e.target.value)}
                      label="Ekip Üyesi"
                    >
                      <MenuItem value="" disabled>
                        <em>Ekip üyesi seçin</em>
                      </MenuItem>
                      {teamMembers.map((member) => (
                        <MenuItem key={member.id} value={member.id}>
                          {member.name} ({member.duty || 'Görev atanmamış'})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bitiş Tarihi (Opsiyonel)"
                    type="date"
                    fullWidth
                    size="small"
                    value={newTaskDueDate}
                    onChange={(e) => setNewTaskDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    InputProps={{
                      startAdornment: (
                        <DateRangeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bitiş Saati (Opsiyonel)"
                    type="time"
                    fullWidth
                    size="small"
                    value={newTaskDueTime}
                    onChange={(e) => setNewTaskDueTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={9}>
                  <TextField
                    label="Görev Açıklaması"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Görev hakkında detaylı açıklama giriniz..."
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ position: 'absolute', top: '15px', left: '10px' }}>
                          <InfoIcon sx={{ color: 'text.secondary', fontSize: '1.2rem', mr: 1 }} />
                        </Box>
                      ),
                      sx: { pl: 4 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {taskImages.map((image, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          width: '76px', 
                          height: '76px', 
                          border: '1px dashed #ccc',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {image.error && (
                          <Tooltip title={image.error}>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                                border: '2px solid #f44336',
                                borderRadius: '4px'
                              }}
                            >
                              <Typography variant="caption" color="error" align="center" sx={{ p: 1, fontWeight: 'bold' }}>
                                Hata
                              </Typography>
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    color: 'error.main'
                                  }
                                }}
                                onClick={() => handleRemoveImage(index)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}
                        
                        {image.preview ? (
                          <>
                            <img 
                              src={image.preview} 
                              alt={`Görev Resmi ${index + 1}`} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                }
                              }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Box
                            component="label"
                            htmlFor={`image-upload-${index}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              id={`image-upload-${index}`}
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handleImageSelect(index, e)}
                              style={{ display: 'none' }}
                            />
                            <AddIcon sx={{ color: 'text.secondary' }} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                  
                  {taskImages.some(img => img.error !== '' && img.preview !== '') && (
                    <Typography variant="caption" color="error" align="center" sx={{ mt: 1 }}>
                      Hatalı resim(ler) var. Lütfen kaldırın veya değiştirin.
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddTask}
                    fullWidth
                  >
                    Görev Ekle
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>
          
          {/* Görevler tablosu */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
              Eklenmiş Görevler
            </Typography>
            <TableContainer 
              component={Paper} 
              variant="outlined" 
              sx={{ 
                borderRadius: 2, 
                minHeight: 200,
                maxHeight: 320
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    {isSequentialSystem && (
                      <TableCell width="10%" align="center">Sıra</TableCell>
                    )}
                    <TableCell width={isSequentialSystem ? "20%" : "25%"}>Ekip Üyesi</TableCell>
                    <TableCell width={isSequentialSystem ? "20%" : "20%"}>Görev Adı</TableCell>
                    <TableCell width="10%">Öncelik</TableCell>
                    <TableCell width="15%">Bitiş Tarihi</TableCell>
                    <TableCell width="20%">Açıklama</TableCell>
                    <TableCell width="10%">Resimler</TableCell>
                    <TableCell width="5%" align="center">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.length > 0 ? (
                    displayRows.map((task) => (
                      <TableRow key={task.id} hover sx={{ '& td': { py: 1 } }}>
                        {isSequentialSystem && (
                          <TableCell align="center">
                            <Chip 
                              label={task.taskOrder} 
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                              {task.teamMemberName || 'Bilinmeyen Üye'}
                        </TableCell>
                        <TableCell>{task.taskName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={priorityOptions.find(p => p.value === task.priority)?.label} 
                            size="small"
                            color={
                              task.priority === 'high' ? 'error' :
                              task.priority === 'medium' ? 'warning' : 'success'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {task.dueDate && task.dueTime 
                            ? `${new Date(task.dueDate).toLocaleDateString('tr-TR')} ${task.dueTime}` 
                            : task.dueDate 
                              ? `${new Date(task.dueDate).toLocaleDateString('tr-TR')} (Saat yok)`
                              : 'Tarih yok'
                          }
                        </TableCell>
                        <TableCell>
                          <Tooltip title={task.description || 'Açıklama eklenmemiş'} arrow placement="top">
                            {task.description ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  maxWidth: '200px',
                                  minHeight: '32px',
                                  lineHeight: '16px'
                                }}
                              >
                                {task.description}
                              </Typography>
                            ) : (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontStyle: 'italic',
                                  color: 'text.secondary',
                                  fontSize: '0.75rem'
                                }}
                              >
                                Açıklama eklenmemiş
                              </Typography>
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {(task.taskImages && task.taskImages.length > 0) || (task.existingImageUrls && task.existingImageUrls.length > 0) ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {/* Yeni eklenen resimler */}
                              {task.taskImages && task.taskImages.map((img, imgIndex) => (
                                <Box 
                                  key={`new-${imgIndex}`}
                                  sx={{ 
                                    width: '30px', 
                                    height: '30px', 
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    border: '1px solid #eee'
                                  }}
                                >
                                  <img 
                                    src={img.preview} 
                                    alt={`Görev Resmi ${imgIndex + 1}`} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                  />
                                </Box>
                              ))}
                              
                              {/* Mevcut resimler */}
                              {task.existingImageUrls && task.existingImageUrls.map((imgUrl, imgIndex) => (
                                <Box 
                                  key={`existing-${imgIndex}`}
                                  sx={{ 
                                    width: '30px', 
                                    height: '30px', 
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    border: '1px solid #eee',
                                    position: 'relative'
                                  }}
                                >
                                  <img 
                                    src={imgUrl} 
                                    alt={`Mevcut Resim ${imgIndex + 1}`} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                  />
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Resim yok
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveTask(task.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={isSequentialSystem ? 8 : 7} 
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Henüz görev eklenmemiş
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Boş satırlar ekleyerek sabit boyut sağla */}
                  {emptyRows > 0 && displayRows.length > 0 && (
                    <TableRow style={{ height: 40 * emptyRows }}>
                      <TableCell colSpan={isSequentialSystem ? 8 : 7} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Sayfalama */}
            {tasks.length > 0 && (
              <TablePagination
                component="div"
                count={tasks.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10]}
                labelRowsPerPage="Sayfa başına görev:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${count === 0 ? 0 : from}-${to} / ${count}`
                }
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          
          {/* Sıralı görev sistemi için bilgilendirme */}
          {isSequentialSystem && tasks.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" icon={<ArrowForwardIcon />}>
                <AlertTitle>Sıralı Görev Sistemi</AlertTitle>
                    Bu sistemde ekip üyeleri görevleri sırayla tamamlar. Bir görev tamamlanmadan diğer göreve geçilemez.
              </Alert>
            </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            size="small"
          >
            İptal
          </Button>
          <Button 
            onClick={handleSaveProject} 
            variant="contained" 
            color="primary"
            disabled={loading || tasks.length === 0}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            size="small"
          >
            {isEditMode ? 'Değişiklikleri Kaydet' : 'Projeyi Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bildirim bileşeni */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateTeamTaskModal; 